import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Infinity } from "ldrs/react";

const PageCommentsWithOptimisticUpdate = ({
  publicId,
  moduleId,
  pageId,
  token,
  currentUser,
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Новые состояния для пагинации
  const [visibleCommentsCount, setVisibleCommentsCount] = useState(5);
  const COMMENTS_PER_PAGE = 5;

  // Функция для получения комментариев страницы
  const fetchComments = useCallback(
    async (showLoading = true) => {
      if (!token || !publicId || !moduleId || !pageId) {
        
        setLoading(false);
        return;
      }

      try {
        if (showLoading) setLoading(true);
        setError("");

        const response = await axios.get(
          `https://us-central1-extent-protocol-a.cloudfunctions.net/getCourse?token=${token}&publicId=${publicId}&_t=${Date.now()}`,
          {
            timeout: 15000,
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );

        let commentsData = [];

        // Извлекаем комментарии из структуры курса
        if (
          response.data &&
          response.data.modules &&
          response.data.modules[moduleId] &&
          response.data.modules[moduleId].pages &&
          response.data.modules[moduleId].pages[pageId] &&
          response.data.modules[moduleId].pages[pageId].comments
        ) {
          const commentsObj =
            response.data.modules[moduleId].pages[pageId].comments;

          // Преобразуем объект комментариев в массив
          commentsData = Object.entries(commentsObj).map(
            ([timestamp, comment]) => ({
              ...comment,
              id: timestamp,
              timestamp: timestamp,
            })
          );

          // Сортируем по времени создания (новые сверху)
          commentsData.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        }

        setComments(commentsData);
      } catch (error) {
        if (error.code === "ECONNABORTED") {
          setError("Превышено время ожидания. Попробуйте еще раз.");
        } else if (error.response) {
          setError(`Ошибка сервера: ${error.response.status}`);
        } else if (error.request) {
          setError("Проблема с сетью. Проверьте подключение к интернету.");
        } else {
          setError("Ошибка загрузки комментариев");
        }

        if (showLoading) {
          setComments([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [token, publicId, moduleId, pageId]
  );

  // Отправка комментария для страницы
  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) {
      setError("Комментарий не может быть пустым");
      return;
    }

    const commentText = newComment.trim();

    try {
      setSubmitting(true);
      setError("");

      const response = await axios.post(
        "https://us-central1-extent-protocol-a.cloudfunctions.net/sendCommentToCourse",
        {
          token,
          publicId,
          text: commentText,
          module_id: moduleId,
          page_id: pageId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 15000,
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Очищаем поле ввода
        setNewComment("");

        // Обновляем комментарии с сервера
        fetchComments(false);
      }
    } catch (error) {
      console.error("Error submitting page comment:", error);

      if (error.code === "ECONNABORTED") {
        setError("Превышено время ожидания отправки. Попробуйте еще раз.");
      } else if (error.response) {
        setError(`Ошибка сервера при отправке: ${error.response.status}`);
      } else if (error.request) {
        setError("Проблема с сетью при отправке. Проверьте подключение.");
      } else {
        setError("Ошибка при отправке комментария. Попробуйте еще раз.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Функции для управления показом комментариев
  const showMoreComments = () => {
    setVisibleCommentsCount((prev) => prev + COMMENTS_PER_PAGE);
  };

  const showLessComments = () => {
    setVisibleCommentsCount(COMMENTS_PER_PAGE);
  };

  // Получаем видимые комментарии
  const visibleComments = comments.slice(0, visibleCommentsCount);
  const hasMoreComments = comments.length > visibleCommentsCount;
  const canShowLess = visibleCommentsCount > COMMENTS_PER_PAGE;

  // Форматирование даты
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date = new Date(timestamp);
      return date.toLocaleString("ru-RU", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "";
    }
  };

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-2 mt-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Комментарии
        </h3>
        <div className="flex justify-center items-center h-32">
          <Infinity
            size="60"
            stroke="4"
            strokeLength="0.15"
            bgOpacity="0.1"
            speed="1.3"
            color="#006400"
          />
        </div>
        <p className="text-center text-gray-500 mt-2">
          Загрузка комментариев...
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-2 mt-8">
      <h3 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-3">
        Комментарии ({comments.length})
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button
            onClick={() => fetchComments()}
            className="ml-2 text-red-600 hover:text-red-800 underline"
          >
            Попробовать снова
          </button>
        </div>
      )}

      {/* Форма добавления комментария */}
      <form onSubmit={handleSubmitComment} className="mb-6">
        <div className="mb-4">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Оставьте ваш комментарий к этой странице..."
            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            rows="4"
            disabled={submitting}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Infinity
                  size="20"
                  stroke="3"
                  strokeLength="0.15"
                  bgOpacity="0.1"
                  speed="1.3"
                  color="white"
                />
                Отправляется...
              </>
            ) : (
              "Отправить"
            )}
          </button>
        </div>
      </form>

      {/* Список комментариев */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">Пока нет комментариев к этой странице</p>
            <p className="text-sm">Будьте первым, кто оставит комментарий!</p>
          </div>
        ) : (
          <>
            {visibleComments.map((comment, index) => (
              <div
                key={comment.id || index}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {comment.fullname?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {comment.fullname || "Пользователь"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(comment.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="ml-11">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {comment.text}
                  </p>
                </div>
              </div>
            ))}

            {/* Кнопки управления показом комментариев */}
            {(hasMoreComments || canShowLess) && (
              <div className="flex justify-center gap-4 pt-4 border-t border-gray-200">
                {hasMoreComments && (
                  <button
                    onClick={showMoreComments}
                    className="px-4 py-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    Показать ещё{" "}
                    {Math.min(
                      COMMENTS_PER_PAGE,
                      comments.length - visibleCommentsCount
                    )}{" "}
                    комментариев
                  </button>
                )}

                {canShowLess && (
                  <button
                    onClick={showLessComments}
                    className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                    Свернуть
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PageCommentsWithOptimisticUpdate;
